import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal, AfterViewInit, ViewChild, } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { PiasLayoutComponent } from '../../../shared/components/pias-layout/pias-layout.component';
import { AdStatusBadgeComponent } from '../marketing-review-components/ad-status-badge/ad-status-badge.component';
import { AuditLogDialogComponent, AuditLogDialogData } from '../marketing-review-components/audit-log-dialog/audit-log-dialog.component';
import { AD_STATUSES, AdStatus, MarketingAd, ReviewQueueRow, AdAuditEntry, MarketingReviewSummary, ReviewStatusFilter,REVIEW_STATUS_FILTERS } from '../marketing-review-interfaces/marketing-review.interface';
import { MarketingReviewService } from '../marketing-review-service/marketing-review.service';
import { MarketingReviewApplication } from '../marketing-review-interfaces/marketing-review.interface';
import { HttpStatusCode } from 'axios';
import { CreateAdService } from '../create-ad/create-ad-service/create-ad-service';
import { SessionStorageService } from '../../../core/services/session-storage/session-storage.service';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { ConfirmPopupComponent } from '../../agent-onboarding/new-rep-application/confirm-popup/confirm-popup.component';
import { SnackbarService } from '../../../shared/services/snackbar/snackbar.service';

/** One KPI tile above the queue table. */
type DashboardStatFilter =
    | 'Draft'
    | 'Compliance Review'
    | 'Partnership Review'
    | 'Marketing Review'
    | 'Submitted'
    | 'Returned'
    | 'Rejected'
    | 'Approved';

interface DashboardStat {
    label: string;
    value: number;
    tone: 'draft' | 'compliance' | 'partnership' | 'marketing'
    | 'pending' | 'changes' | 'approved' | 'rejected';
    /** Material icon ligature shown in the tile's disc. */
    icon: string;
    /** Status this tile filters the table down to when clicked. */
    filter: DashboardStatFilter;

}

/**
 * Marketing Review dashboard — the module's landing page for both audiences, scoped by role in
 * `MarketingReviewService.dashboardAds()`:
 *
 *   - Marketing team — every user's submitted ads, with the outcomes that followed. No "Create Ad".
 *   - Creator — only the ads they raised, drafts included, plus "Create Ad". A draft row opens the
 *     form for editing rather than the review page.
 *
 * Expanding a row lists the files attached to *that* advertisement — a single application often
 * carries a deck, its key visual and the signed application form, and the reviewer needs to see
 * the formats before opening it.
 */
@Component({
    selector: 'app-marketing-review-dashboard',
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatSelectModule,
        MatTooltipModule,
        PiasLayoutComponent,
        AdStatusBadgeComponent,
        MatTooltipModule,
        MatPaginatorModule,
        PiasLayoutComponent,
        AdStatusBadgeComponent
    ],
    templateUrl: './marketing-review-dashboard.component.html',
    styleUrl: './marketing-review-dashboard.component.scss'
})
export class MarketingReviewDashboardComponent implements OnInit, AfterViewInit {
    ngAfterViewInit(): void {
        // throw new Error('Method not implemented.');
    }

    private router = inject(Router);
    private matDialog = inject(MatDialog);
    private marketingReviewService = inject(MarketingReviewService);
    private marketingReviewApplication!: MarketingReviewApplication[];
    private CreateAdService = inject(CreateAdService);
    private sessionStorageService = inject(SessionStorageService);



    protected readonly setSprinner = signal(false);

    /** Marketing team view; otherwise the creator view. Fixed for the page's lifetime. */
    protected readonly isReviewer: boolean = this.marketingReviewService.isMarketingReviewer();

    /** The marketing team only ever sees submitted ads, so Draft is not a useful filter there. */
    // protected readonly statuses: ReadonlyArray<AdStatus> = this.isReviewer
    //     ? AD_STATUSES.filter((status) => status !== 'Draft')
    //     : AD_STATUSES;
    protected readonly statuses = this.isReviewer
    ? REVIEW_STATUS_FILTERS.filter(status => status.value !== 'Draft')
    : REVIEW_STATUS_FILTERS;

    protected summary: MarketingReviewSummary = {
        totalAds: 0,
        pendingReview: 0,
        approved: 0,
        returned: 0,
        rejected: 0,
        branches: []
    };

    protected stats: DashboardStat[] = [];

    /** The whole queue, and the subset surviving the filters. */
    private allRows: ReviewQueueRow[] = [];
    protected rows: ReviewQueueRow[] = [];

    /** Expanded rows, keyed by ad id. */
    protected expanded: Record<number, boolean> = {};

    protected branchFilter: string = '';
    protected statusFilter: ReviewStatusFilter = '';
    protected branches: string[] = [];
    private loadedDocumentRows = new Set<number>();
    private snackbarService = inject(SnackbarService);
    branchId: any[] = [];
    branchNameMap = new Map<string, string>();

    @ViewChild(MatPaginator) paginator!: MatPaginator;

    protected searchQuery: string = '';
    protected pageSize = 5;
    protected pageIndex = 0;
    protected paginatedRows: ReviewQueueRow[] = [];
    protected selectedStatFilter: DashboardStatFilter | '' = '';

    ngOnInit(): void {
        this.getBranches();
        this.loadDashboardData();
    }
    async getBranches(): Promise<void> {
        const res = await this.CreateAdService.getBranches();

        this.branchId = res.responsedata;

        this.branchNameMap = new Map(
            this.branchId.map(branch => [String(branch.branchid), branch.branchname]));
    }
    getBranchName(branchId: string | number): string {
        return this.branchNameMap.get(String(branchId)) ?? String(branchId ?? '');
    }
    editApplication(row: any, event: Event): void {
        event.stopPropagation();

        const applicationId = row.ad.marketingReviewApplicationId ?? row.ad.id;

        if (!applicationId) {
            return;
        }

        this.router.navigate(['/marketing-review/create-ad'], {
            queryParams: {
                marketingReviewApplicationId: applicationId
            }
        });
    }
    async deleteDraft(row: ReviewQueueRow, event: Event): Promise<void> {
        event.stopPropagation();

        if (row.ad.status !== 'Draft') {
            return;
        }

        const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
            width: '500px',
            disableClose: true,
            data: {
                message: 'Do you want to delete this draft application?',
                hidecancel: false
            }
        });

        dialogRef.afterClosed().subscribe(async result => {
            if (result?.data !== true) {
                return;
            }

            this.setSprinner.set(true);

            const data = {
                applicationId: row.ad.id,
                piasRepCode: this.sessionStorageService.getCurrentRepCode()
            };

            const responseData = await this.marketingReviewService
                .deleteMarketingReviewApplication(data)
                .then(response => JSON.parse(response))
                .catch(error => error);

            this.setSprinner.set(false);

            if (responseData.status === HttpStatusCode.Ok) {
                this.allRows = this.allRows.filter(
                    (item: ReviewQueueRow) => item.ad.id !== row.ad.id);

                this.rows = this.rows.filter(
                    (item: ReviewQueueRow) => item.ad.id !== row.ad.id);

                this.refreshRows();

                this.snackbarService.openSuccessSnackBar('Draft application deleted successfully!');
            } else {
                this.snackbarService.openFailureSnackBar('Unable to delete draft application.');
            }
        });
    }

    async loadDashboardData(): Promise<void> {
        this.setSprinner.set(true);

        const repcode = this.sessionStorageService.getCurrentRepCode();
        const responseData = await this.marketingReviewService
            .getAllMarketingReview(repcode)
            .then((response) => { return JSON.parse(response); })
            .catch((error) => { return error; });

        this.setSprinner.set(false);

        if (Array.isArray(responseData.data)) {

            const applications: MarketingReviewApplication[] = responseData.data;

            this.allRows = applications.map(application => this.mapApplicationToRow(application));
            this.branches = [...new Set(this.allRows.flatMap(row => row.ad.branches))];
            this.refreshRows()


        } else {
            console.error('Failed to load Marketing Review applications', responseData);
        }

    }
    private mapApplicationToRow(application: MarketingReviewApplication): ReviewQueueRow {
        const id = application.marketingReviewApplicationId;

        const status = this.normalizeAdStatus(application.status);
        const submitter = application.submittedBy || application.applicantRepCode || 'Unknown';

        // const createdOn = application.createdDate || application.dateOfApplication || '';
        const rawDate = application.createdDate || application.dateOfApplication || '';
        const createdOn = rawDate ? rawDate.split('-').reverse().join('-') : '';

        const submittedOn = status === 'Draft' ? null : createdOn || null;

        const ad: MarketingAd = {
            id,
            referenceNo: application.approvalNo || `MR-${id}`,
            title: application.marketingTitle || 'Untitled',

            branches: application.applicantBranchId != null ? [this.getBranchName(application.applicantBranchId)] : [],
            targetRecipients: application.targetRecepients || '',
            expectedDisseminationDate: application.expectedDateOfDissemination || '',
            disseminationNote: application.expectedCommentOfDissemination || '',
            modeOfDissemination: application.modeOfDissemination || '',

            // The API provides a numeric material type ID, not the code.
            materialType: '',

            // These fields aren't provided by this API.
            channel: 'Collateral',
            category: '',
            targetAudience: '',
            campaignStartDate: '',
            campaignEndDate: '',
            description: '',
            bodyCopy: '',
            callToAction: '',
            disclaimers: '',
            previewImageUrl: '',
            attachments: (application.documents ?? []).map(doc => ({
                id: doc.documentId,
                fileName: doc.documentName,
                fileType: doc.documentName.split('.').pop() ?? '',
                fileSizeKb: 0,
                url: ''
            })),
            status,
            complianceStatus: application.compliance || '',
            partnershipStatus: application.partnership || '',
            marketingStatus: application.marketing || '',
            isProductRelated: application.questionsProductsSolutions,
            createdBy: submitter,
            createdByRepCode: application.submittedByRepCode || application.applicantRepCode || '',

            createdOn,
            submittedOn,
            lastReviewedOn: application.updatedDate || null,
        };

        const initials = submitter.split(/\s+/).filter(Boolean).slice(0, 2).map(name => name.charAt(0).toUpperCase()).join('');

        const submittedTime = submittedOn ? new Date(submittedOn).getTime() : 0;

        const daysWaiting = submittedTime && !Number.isNaN(submittedTime)
            ? Math.max(0, Math.floor((Date.now() - submittedTime) / (1000 * 60 * 60 * 24))) : 0;

        return {
            ad,
            initials,
            formats: [],
            files: (application.documents ?? []).map(doc => ({
                attachment: {
                    id: doc.documentId,
                    fileName: doc.documentName,
                    fileType: doc.documentName.split('.').pop() ?? '',
                    fileSizeKb: 0,
                    url: ''
                },
                kind: 'other',
                format: doc.documentName.split('.').pop()?.toUpperCase() ?? 'FILE',
                icon: 'description',
                tone: 'other',
                meta: doc.documentDate,
                thumbnailUrl: ''
            })),
            daysWaiting
        };
    }


    private normalizeAdStatus(value: string): AdStatus {
        const normalized = (value || '')
            .trim()
            .toLowerCase()
            .replace(/[\s-]+/g, '_');

        switch (normalized) {
            case 'draft':
                return 'Draft';

            case 'submitted':
            case 'pending':
            case 'pending_review':
                return 'Submitted';

            case 'in_review':
            case 'under_review':
                return 'In_Review';

            case 'approved':
                return 'Approved';

            case 'returned':
            case 'changesrequested':
                return 'Returned';

            case 'rejected':
                return 'Rejected';

            default:
                console.warn('Unknown application status:', value);
                return 'Submitted';
        }
    }
    private isStagePending(value: string | null | undefined): boolean {
        const status = (value || '')
            .trim()
            .toLowerCase()
            .replace(/[\s-]+/g, '_');

        return [
            'pending',
            'submitted',
            'in_review',
            'under_review',
            'pending_review',
            'in_progress'
        ].includes(status);
    }

    private matchesStatusFilter(ad: MarketingAd): boolean {
    const compliance = (ad.complianceStatus || '').toLowerCase().trim();
    const partnership = (ad.partnershipStatus || '').toLowerCase().trim();
    const marketing = (ad.marketingStatus || '').toLowerCase().trim();

    const filters: Record<string, boolean> = {
        'Draft': ad.status === 'Draft',

        'Compliance Review': compliance === 'review',
        'Compliance Approved': compliance === 'approved',
        'Compliance Rejected': compliance === 'rejected',
        'Compliance Requested Change': this.isRequestedChange(compliance),

        'Partnership Review':
            ad.isProductRelated === true && partnership === 'review',
        'Partnership Approved': partnership === 'approved',
        'Partnership Rejected': partnership === 'rejected',
        'Partnership Requested Change': this.isRequestedChange(partnership),

        'Marketing Review':
            compliance === 'approved' &&
            (!ad.isProductRelated || partnership === 'approved'),

        'Marketing Approved': marketing === 'approved',
        'Marketing Rejected': marketing === 'rejected',
        'Marketing Requested Change': this.isRequestedChange(marketing)
    };

    return filters[this.statusFilter] ?? true;
}

private isRequestedChange(status: string): boolean {
    return [
        'returned',
        'requested_change',
        'change_requested',
        'changes_requested'
    ].includes(status);
}
    /** Re-derives the tiles and the table from the current filters. */
    protected refreshRows(): void {
        this.rows = this.allRows.filter((row) => this.matchesFilters(row.ad));
        this.pageIndex = 0;
        this.updatePaginatedRows();

        /*
         * Tile counts deliberately ignore the status/pending filter and honour only the branch
         * filter. Deriving them from the filtered rows instead would zero every tile except the
         * selected one, which would make the tiles unusable as toggles.
         */
        const visible: MarketingAd[] = this.allRows
            .map((row) => row.ad)
            .filter((ad) => !this.branchFilter || ad.branches.includes(this.branchFilter));

        // this.stats = [
        //     {
        //         label: 'Pending review',
        //         tone: 'pending',
        //         icon: 'hourglass_top',
        //         filter: 'pending',
        //         value: visible.filter((ad) => this.marketingReviewService.isPendingReview(ad.status)).length
        //     },
        //     {
        //         label: 'Changes requested',
        //         tone: 'changes',
        //         icon: 'undo',
        //         filter: 'Returned',
        //         value: visible.filter((ad) => ad.status === 'Returned').length
        //     },
        //     {
        //         label: 'Approved',
        //         tone: 'approved',
        //         icon: 'check_circle',
        //         filter: 'Approved',
        //         value: visible.filter((ad) => ad.status === 'Approved').length
        //     },
        //     {
        //         label: 'Rejected',
        //         tone: 'rejected',
        //         icon: 'cancel',
        //         filter: 'Rejected',
        //         value: visible.filter((ad) => ad.status === 'Rejected').length
        //     }
        // ];
        this.stats = [
    ...(!this.isReviewer ? [{
        label: 'Draft',
        value: visible.filter(ad => ad.status === 'Draft').length,
        tone: 'draft' as const,
        icon: 'edit',
        filter: 'Draft' as DashboardStatFilter
    }] : []),

    {
        label: 'Compliance Review',
        value: visible.filter(ad =>
            this.isStagePending(ad.complianceStatus)
        ).length,
        tone: 'compliance',
        icon: 'verified_user',
        filter: 'Compliance Review'
    },
    {
        label: 'Partnership Review',
        value: visible.filter(ad =>
            this.isStagePending(ad.partnershipStatus)
        ).length,
        tone: 'partnership',
        icon: 'handshake',
        filter: 'Partnership Review'
    },
    {
        label: 'Marketing Review',
        value: visible.filter(ad =>
            this.isStagePending(ad.marketingStatus)
        ).length,
        tone: 'marketing',
        icon: 'campaign',
        filter: 'Marketing Review'
    },
    {
        label: 'Submitted',
        value:
            visible.filter(ad => ad.status === 'Submitted').length +
            visible.filter(ad => this.isStagePending(ad.complianceStatus)).length +
            visible.filter(ad => this.isStagePending(ad.partnershipStatus)).length +
            visible.filter(ad => this.isStagePending(ad.marketingStatus)).length,
        tone: 'pending',
        icon: 'send',
        filter: 'Submitted'
    },
    {
        label: 'Requested Change',
        value: visible.filter(ad => ad.status === 'Returned').length,
        tone: 'changes',
        icon: 'undo',
        filter: 'Returned'
    },
    {
        label: 'Rejected',
        value: visible.filter(ad => ad.status === 'Rejected').length,
        tone: 'rejected',
        icon: 'cancel',
        filter: 'Rejected'
    },
    {
        label: 'Approved',
        value: visible.filter(ad => ad.status === 'Approved').length,
        tone: 'approved',
        icon: 'check_circle',
        filter: 'Approved'
    }
];

        // The tiles are measured against everything currently in scope, so the rings still read
        // as shares when a branch filter is applied.
        this.statTotal = visible.length;
    }
    /**
 * Updates the rows displayed on the current page.
 */
    private updatePaginatedRows(): void {
        const startIndex = this.pageIndex * this.pageSize;
        const endIndex = startIndex + this.pageSize;

        this.paginatedRows = this.rows.slice(startIndex, endIndex);
    }

    /**
     * Handles first/previous/next/last page navigation and page-size changes.
     */
    protected onPageChange(event: PageEvent): void {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;

        this.updatePaginatedRows();
    }

    /**
     * Clears the search field and reapplies the remaining filters.
     */
    protected clearSearch(): void {
        this.searchQuery = '';
        this.refreshRows();
    }

    /** Denominator behind the tile rings. */
    protected statTotal: number = 0;

    /** Share of the in-scope total, for the ring sweep and the tooltip. */
    protected pctOf(value: number): string {
        if (!this.statTotal) {
            return '0%';
        }
        return Math.round((value / this.statTotal) * 100) + '%';
    }

    /** Conic sweep encoding the tile's share — the gradient angle is the data. */
    protected ringGradient(value: number): string {
        return `conic-gradient(var(--c1) ${this.pctOf(value)}, var(--track) 0)`;
    }

    /**
     * Tiles double as status filters, matching how Adviser Maintenance's stat cards behave.
     * Clicking the active tile clears the filter.
     */
    protected toggleStat(stat: DashboardStat): void {
        // const alreadyOn: boolean = this.isStatActive(stat);

        // if (alreadyOn) {
        //     this.statusFilter = '';
        //     this.pendingOnly = false;
        // } else if (stat.filter === 'pending') {
        //     this.statusFilter = '';
        //     this.pendingOnly = true;
        // } else {
        //     this.statusFilter = stat.filter;
        //     this.pendingOnly = false;
        // }
        this.selectedStatFilter =
        this.selectedStatFilter === stat.filter ? '' : stat.filter;

        this.refreshRows();
    }

    protected isStatActive(stat: DashboardStat): boolean {
        // return stat.filter === 'pending'
        //     ? this.pendingOnly
        //     : this.statusFilter === stat.filter;
        return this.selectedStatFilter === stat.filter;
    }

    /** Set by the "Pending review" tile, which spans two statuses rather than one. */
    // protected pendingOnly: boolean = false;

    private matchesFilters(ad: MarketingAd): boolean {
        if (this.branchFilter && !ad.branches.includes(this.branchFilter)) {
            return false;
        }
        // if (this.statusFilter && ad.status !== this.statusFilter) {
        //     return false;
        // }
        if (this.statusFilter && !this.matchesStatusFilter(ad)) {
    return false;
}
        // if (this.pendingOnly && !this.marketingReviewService.isPendingReview(ad.status)) {
        //     return false;
        // }
        if (this.selectedStatFilter) {
    switch (this.selectedStatFilter) {
        case 'Draft':
            if (ad.status !== 'Draft') return false;
            break;

        case 'Compliance Review':
            if (!this.isStagePending(ad.complianceStatus)) return false;
            break;

        case 'Partnership Review':
            if (!this.isStagePending(ad.partnershipStatus)) return false;
            break;

        case 'Marketing Review':
            if (!this.isStagePending(ad.marketingStatus)) return false;
            break;

        case 'Submitted':
            if (
                ad.status !== 'Submitted' &&
                !this.isStagePending(ad.complianceStatus) &&
                !this.isStagePending(ad.partnershipStatus) &&
                !this.isStagePending(ad.marketingStatus)
            ) {
                return false;
            }
            break;

        case 'Returned':
            if (ad.status !== 'Returned') return false;
            break;

        case 'Rejected':
            if (ad.status !== 'Rejected') return false;
            break;

        case 'Approved':
            if (ad.status !== 'Approved') return false;
            break;
    }
}
        // Search by application title, branch, submitter, rep code,
        // reference number, or status.
        const query = this.searchQuery.trim().toLowerCase();

        if (query) {
            const searchableText = [
                ad.title,
                ad.branches.join(' '),
                ad.createdBy,
                ad.createdByRepCode,
                ad.referenceNo,
                ad.status
            ]
                .filter((value) => value != null)
                .join(' ')
                .toLowerCase();

            if (!searchableText.includes(query)) {
                return false;
            }
        }
        return true;
    }

    protected resetFilters(): void {
        this.branchFilter = '';
        this.statusFilter = '';
        // this.pendingOnly = false;
         this.selectedStatFilter = '';
        this.searchQuery = '';
        this.refreshRows();
    }

    protected get hasActiveFilters(): boolean {
        return !!(this.branchFilter || this.statusFilter || this.selectedStatFilter  || this.searchQuery.trim());
    }

    // ── Row expansion ───────────────────────────────────────────────────────────

    protected isExpanded(row: ReviewQueueRow): boolean {
        return !!this.expanded[row.ad.id];
    }

    protected toggleExpand(row: ReviewQueueRow, event?: Event): void {
        event?.stopPropagation();
        const id: number = row.ad.id;
        this.expanded = { ...this.expanded, [id]: !this.expanded[id] };
    }

    // ── Row presentation ────────────────────────────────────────────────────────

    /** `Orchard, Bishan`, or a count once there are too many to fit the cell. */
    protected branchLabel(row: ReviewQueueRow): string {
        const branches: string[] = row.ad.branches;
        if (!branches.length) {
            return '—';
        }
        return branches.length <= 2 ? branches.join(', ') : `${branches.length} branches`;
    }

    /** `PPT +1` — the leading format with a count of the other kinds attached. */
    protected formatLabel(row: ReviewQueueRow): string {
        if (!row.formats.length) {
            return '—';
        }
        const [first, ...rest] = row.formats;
        return rest.length ? `${first} +${rest.length}` : first;
    }

    /** `3 files` / `1 file`, blank when nothing is attached. */
    protected fileCountLabel(row: ReviewQueueRow): string {
        const count: number = row.files.length;
        if (!count) {
            return 'No files';
        }
        return count === 1 ? '1 file' : `${count} files`;
    }

    // protected isOverdue(row: ReviewQueueRow): boolean {
    //     return this.marketingReviewService.isPendingReview(row.ad.status) && row.daysWaiting > 5;
    // }

    // ── Actions ─────────────────────────────────────────────────────────────────

    protected async openAudit(row: ReviewQueueRow, event?: Event): Promise<void> {
        event?.stopPropagation();

        const response = await this.marketingReviewService.gethistory(row.ad.id);

        const entries: AdAuditEntry[] = (response.responsedata || []).map((item: any) => ({
            at: this.parseLogDate(item.logDate),
            action: item.action,
            detail: item.remarks || '',
            actor: item.owner || item.role || '',
            tone: this.getAuditTone(item.action)
        }));

        const data: AuditLogDialogData = {
            referenceNo: row.ad.referenceNo,
            title: row.ad.title,
            submitter: `${row.ad.createdBy}${row.ad.createdByRepCode ? ` (${row.ad.createdByRepCode})` : ''}`,
            entries
        };

        this.matDialog.open(AuditLogDialogComponent, {
            width: '880px',
            maxWidth: '96vw',
            maxHeight: '86vh',
            panelClass: 'mr-audit-dialog-panel',
            data
        });
    }
    private parseLogDate(logDate: string): string {
        const [datePart, timePart] = logDate.split(' ');
        const [day, month, year] = datePart.split('-');

        return `${year}-${month}-${day}T${timePart}`;
    }
    private getAuditTone(action: string): AdAuditEntry['tone'] {
        const value = action?.toLowerCase() ?? '';

        if (value.includes('submitted')) {
            return 'submitted';
        }
        if (value.includes('approved')) {
            return 'approved';
        }
        if (value.includes('returned')) {
            return 'returned';
        }
        if (value.includes('rejected')) {
            return 'rejected';
        }
        return 'info';
    }

    protected createAd(): void {
        this.router.navigate(['/marketing-review/create-ad']);
    }

    /** A draft opens the form for editing; anything submitted opens the review page. */
    protected openAd(ad: MarketingAd, event?: Event): void {
        event?.stopPropagation();
        const route: string = ad.status === 'Draft' ? '/marketing-review/create-ad' : '/marketing-review/review-ad';
        this.router.navigate([route], {
            state: {
                applicationId: ad.id,
            }
        });
    }
}
